import { addDaysYmd, localDateOf } from './mcp/timezone';

/**
 * The essential report set (PRD v2 §7 V4, feature matrix "Reporting / insights") — one
 * shape every report speaks, so fifteen reports cost about what three would and none of
 * them drifts into its own idea of a date range, a total or an empty cell.
 *
 * NOT a report builder. The matrix skips that on purpose — "AI/MCP conversational query is
 * the better answer". The catalogue below is CLOSED and server-defined; a shop picks from
 * it, it doesn't assemble one. What this shape buys is uniformity, not configurability.
 */

/** How a cell is formatted. The renderer owns formatting so no report reinvents it. */
export type ReportCellKind =
  | 'text'
  | 'money'
  | 'number'
  | 'percent'
  | 'date'
  | 'duration'
  | 'link';

export interface ReportColumn {
  key: string;
  label: string;
  kind: ReportCellKind;
  /** Sum this column into the footer. Only where a sum means something — never a percent. */
  total?: boolean;
  /** For 'link' columns: the route the cell navigates to, `:id` replaced from `row[idKey]`. */
  href?: string;
  idKey?: string;
}

export type ReportCell = string | number | null;
export type ReportRow = Record<string, ReportCell>;

export interface ReportFigure {
  label: string;
  value: number | string | null;
  kind: ReportCellKind;
  /** Colour, when the number carries a verdict rather than just a value. */
  tone?: 'brand' | 'success' | 'danger' | 'warn';
}

export interface ReportResult {
  columns: ReportColumn[];
  rows: ReportRow[];
  /** Headline figures above the table — what the owner reads if they read nothing else. */
  figures: ReportFigure[];
  /**
   * What this report can NOT tell you, and why. A report that quietly omits its own
   * limitations is how a shop makes a decision on a number that never meant what they
   * thought. Rendered, not buried.
   */
  caveats: string[];
}

export const REPORT_GROUPS = ['money', 'work', 'clients', 'inventory'] as const;
export type ReportGroup = (typeof REPORT_GROUPS)[number];

export const REPORT_KEYS = [
  // Money
  'revenue-by-month',
  'ar-aging',
  'payments-received',
  'tax-collected',
  // Work
  'job-profitability',
  'tech-profitability',
  'recurring-profitability',
  'project-profitability',
  'unbilled-work',
  'expenses',
  // Clients
  'top-customers',
  'quote-conversion',
  'lead-sources',
  // Inventory
  'inventory-valuation',
  'parts-usage',
] as const;
export type ReportKey = (typeof REPORT_KEYS)[number];

export interface ReportMeta {
  key: ReportKey;
  title: string;
  group: ReportGroup;
  /** One line an owner reads to know whether this is the one they want. */
  blurb: string;
  /** False for as-of-now reports (aging, stock on hand) — a date range would be a lie. */
  ranged: boolean;
}

/** Rows a person can read out loud, in the order an owner thinks about their business. */
export const GROUP_LABEL: Record<ReportGroup, string> = {
  money: 'Money',
  work: 'Work and profit',
  clients: 'Customers',
  inventory: 'Parts',
};

/** `YYYY-MM` → "Mar 2026". Months are the spine of most of these. */
export function monthLabel(ym: string): string {
  const [y, m] = ym.split('-').map(Number);
  if (!y || !m) return ym;
  return new Date(Date.UTC(y, m - 1, 1)).toLocaleDateString('en-US', {
    month: 'short',
    year: 'numeric',
    timeZone: 'UTC',
  });
}

/**
 * An inclusive range of calendar days in words, as a person writes it: "Sep 1–28, 2026",
 * "Jan 1 – Aug 10, 2026", "Oct 1, 2025 – Sep 28, 2026", or one day, "Sep 3, 2026".
 *
 * Read the way `monthLabel` reads a month: the parts of a `YYYY-MM-DD`, as a calendar day at
 * UTC midnight, printed in UTC. Never through the browser's zone, which would move a day for
 * anyone west of Greenwich.
 */
export function rangeLabel(from: string, to: string): string {
  const [fy, fm, fd] = from.split('-').map(Number) as [number, number, number];
  const [ty, tm, td] = to.split('-').map(Number) as [number, number, number];
  const monthDay = (y: number, m: number, d: number) =>
    new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      timeZone: 'UTC',
    });
  const start = monthDay(fy, fm, fd);
  if (from === to) return `${start}, ${fy}`;
  if (fy === ty && fm === tm) return `${start}–${td}, ${fy}`;
  if (fy === ty) return `${start} – ${monthDay(ty, tm, td)}, ${fy}`;
  return `${start}, ${fy} – ${monthDay(ty, tm, td)}, ${ty}`;
}

/**
 * Margin as a percentage of revenue, or null when there is no revenue to be a percentage
 * of. Returning 0 for "no revenue yet" would report a break-even job that never billed.
 */
export function marginPct(revenueCents: number, costCents: number): number | null {
  if (revenueCents <= 0) return null;
  return ((revenueCents - costCents) / revenueCents) * 100;
}

/** Ranges a shop actually asks for, in the words they'd use. */
export const REPORT_PRESETS = [
  { key: 'mtd', label: 'This month' },
  { key: 'last-month', label: 'Last month' },
  { key: 'qtd', label: 'This quarter' },
  { key: 'ytd', label: 'This year' },
  { key: 'last-12', label: 'Last 12 months' },
] as const;
export type ReportPresetKey = (typeof REPORT_PRESETS)[number]['key'];

/**
 * First of a month as `YYYY-MM-DD`, with a 1-based month that may be out of range —
 * `firstOfMonth(2026, 0)` is December 2025 and `firstOfMonth(2026, 13)` is January 2027.
 *
 * String arithmetic rather than `Date` on purpose: every preset below is a statement about a
 * CALENDAR, and the moment a `Date` is involved it acquires an instant, an offset and a DST
 * rule that the calendar question does not have.
 */
export function firstOfMonth(year: number, month: number): string {
  const y = year + Math.floor((month - 1) / 12);
  const m = (((month - 1) % 12) + 12) % 12;
  return `${String(y).padStart(4, '0')}-${String(m + 1).padStart(2, '0')}-01`;
}

/** Last day of a month — the day before the first of the next one, so leap years are free. */
export function lastOfMonth(year: number, month: number): string {
  return addDaysYmd(firstOfMonth(year, month + 1), -1);
}

/**
 * The twelve calendar months ending with the month that holds `to`: from the first of the
 * month 11 months earlier, through `to`. What "Last 12 months" means (`presetRange`), and the
 * Revenue chart's window whatever the period (`revenueChartWindow`), written once so the two
 * cannot drift: for Last 12 months the chart's twelve months ARE the period.
 *
 * `m - 11`, not `y - 1` at the same month. Twelve BUCKETS is what the label promises, and
 * anchoring a year back on the same month gives thirteen: Aug'25 through Aug'26 inclusive.
 * Every ranged report reading the preset counted an extra month of history into its totals.
 * (Behaviour changed deliberately 2026-08-13, ticket 023 — `main` had the 13-month version
 * and three reviewers flagged it independently.)
 */
export function twelveMonthsTo(to: string): { from: string; to: string } {
  const [y, m] = to.split('-').map(Number) as [number, number];
  return { from: firstOfMonth(y, m - 11), to };
}

/**
 * A named preset resolved to an inclusive `YYYY-MM-DD` range **in the shop's timezone**.
 *
 * `tz` is not a nicety. Built from the browser's UTC parts, "This month" on the last evening
 * of a month in New York returned the NEXT month, one day wide — 2026-08-01..2026-08-01 at
 * 21:00 on 31 July — so an owner checking the month's takings after dinner met an empty
 * report on the one evening they were most likely to look. Month, quarter and year all have
 * that failure mode, so all three resolve from one org-local "today".
 *
 * `now` is injectable so the boundary cases are testable without stubbing the clock.
 */
export function presetRange(
  key: ReportPresetKey,
  tz: string,
  now: Date = new Date(),
): { from: string; to: string } {
  const today = localDateOf(now, tz);
  const [y, m] = today.split('-').map(Number) as [number, number, number];
  switch (key) {
    case 'mtd':
      return { from: firstOfMonth(y, m), to: today };
    case 'last-month':
      return { from: firstOfMonth(y, m - 1), to: lastOfMonth(y, m - 1) };
    case 'qtd':
      return { from: firstOfMonth(y, Math.floor((m - 1) / 3) * 3 + 1), to: today };
    case 'ytd':
      return { from: `${String(y).padStart(4, '0')}-01-01`, to: today };
    case 'last-12':
      return twelveMonthsTo(today);
  }
}

/**
 * The same days a year earlier (the Revenue page's one comparison, spec Q1): each date a
 * calendar year back. A day the earlier month does not have becomes that month's last day, so
 * 29 February becomes 28 February. Sep 1–28, 2026 → Sep 1–28, 2025; a leap February, Feb 1–29,
 * 2028 → Feb 1–28, 2027.
 *
 * Read literally, the other way round leaves the leap day out: Feb 1–28, 2029 → Feb 1–28, 2028,
 * without Feb 29, 2028 (ticket 04's gap note). The page prints the comparison period, so what
 * is compared is always said.
 *
 * Calendar arithmetic, as every preset here is. The server reads the answer in the shop's zone
 * as it reads the period, so each range is resolved on its own days (LESSONS 2026-09-30).
 */
export function yearEarlierRange(range: { from: string; to: string }): {
  from: string;
  to: string;
} {
  return { from: dayAYearEarlier(range.from), to: dayAYearEarlier(range.to) };
}

/**
 * One `YYYY-MM-DD` a calendar year back, capped at the last day of that earlier month. The
 * rule `yearEarlierRange` applies to each end; the demo's history dates its same-day job by it.
 */
export function dayAYearEarlier(day: string): string {
  const [y, m, d] = day.split('-').map(Number) as [number, number, number];
  const last = lastOfMonth(y - 1, m);
  const lastDay = Number(last.slice(8));
  return `${last.slice(0, 8)}${String(Math.min(d, lastDay)).padStart(2, '0')}`;
}

/**
 * One `YYYY-MM` a calendar year back: `2026-09` → `2025-09`. The month a Revenue chart month
 * is compared with, which the server finds a year-earlier figure under and the page names.
 */
export function monthAYearEarlier(month: string): string {
  const year = Number(month.slice(0, 4)) - 1;
  return `${String(year).padStart(4, '0')}${month.slice(4)}`;
}
